import type { ReactNode } from 'react';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { FormattedName } from '../../components/FormattedName';
import { GenericAssignReviewerBadge } from '../../components/ReviewerComponents';
import type { AuditTrailStep } from './types';

const resolveActorFallback = (
    step: AuditTrailStep,
    unknownUserLabel: string,
    pendingLabel: string,
    noPublisherLabel: string,
): string => {
    if (step.state !== 'upcoming') {
        return unknownUserLabel;
    }
    if (step.id === 'published') {
        return noPublisherLabel;
    }
    return pendingLabel;
};

// Renders the actor for a row: an inline "assign reviewer" pill when a reviewer is
// still expected, the formatted name when known, or a state-specific fallback label.
export const renderUserPrimary = (
    step: AuditTrailStep,
    architectureId: string,
    item: JobItemDetails,
    isArchived: boolean,
    onInviteReviewer: (() => void) | undefined,
    rowTextClass: string,
    unknownUserLabel: string,
    pendingLabel: string,
    noPublisherLabel: string,
    showReviewerPill: boolean,
    badgeHeightClass = '!h-[26px]',
    textSizeClass = 'text-sm',
): ReactNode => {
    if (showReviewerPill) {
        return (
            <GenericAssignReviewerBadge
                badgeClassName={`${badgeHeightClass} tracking-tight hover:!border-gray-400`}
                chromeVariant="gray"
                disabled={isArchived}
                isSelected={false}
                item={item}
                itemType={item.type}
                jobArchitectureId={architectureId}
                jobClusterId={item.parentId}
                onInvite={onInviteReviewer}
                onlyAssignReviewer={true}
                showOnlyWhenUnassigned={false}
            />
        );
    }
    if (step.actor !== null) {
        return (
            <span className={`truncate ${textSizeClass} font-medium tracking-tight ${rowTextClass}`}>
                <FormattedName defaultText={unknownUserLabel} reviewer={step.actor} />
            </span>
        );
    }
    const fallback = resolveActorFallback(step, unknownUserLabel, pendingLabel, noPublisherLabel);
    return <span className={`truncate ${textSizeClass} font-medium tracking-tight ${rowTextClass}`}>{fallback}</span>;
};
